import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  PageHeader, Button, IconButton, TextArea, Select, Banner, EmptyState, Spinner, useCkToast,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import api from '../../services/api.js';
import { ALLOWED_FRAMES, FRAME_LABELS, SECTION_LABELS, formatMaterial } from './AmericanExams.jsx';
import { SECTION_OPTIONS, emptyQuestion, normalizeLoadedQuestions, sectionSummary } from './AmericanExamEditor.jsx';
import { Row } from '../../next/ui';

/** שאלה חסרה (טקסט אפשרות / תשובה נכונה) — מסומנת בסרגל */
const isIncomplete = (q) => !q.options.every((o) => String(o || '').trim()) || ![1, 2, 3].includes(Number(q.correctOption));

/** סרגל השאלות: קבוצה לכל רצף שאלות מאותו סעיף */
function navGroups(questions) {
  const groups = [];
  questions.forEach((q, i) => {
    let g = groups[groups.length - 1];
    if (!g || g.key !== q.sectionKey) {
      g = { key: q.sectionKey, label: SECTION_LABELS[q.sectionKey] || q.sectionKey, items: [] };
      groups.push(g);
    }
    g.items.push({ q, i });
  });
  return groups;
}

/**
 * עריכת מבחן אמריקאי — new design. Same rules and endpoint as AmericanExamEditor: per question section, text,
 * 3 options and the correct one; reorder / insert / delete; save all (PUT …/questions) with the same validation.
 */
export default function AmericanExamEditorNext() {
  const toast = useCkToast();
  const { frame: frameParam, examId } = useParams();
  const frame = String(frameParam || '').trim();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const cardRefs = useRef([]);
  const rootRef = useRef(null);
  const scrollRef = useRef(null);
  const [screenHeight, setScreenHeight] = useState(null);
  const listPath = `/american-exams/${frame || 'daf_yomi'}`;

  const load = useCallback(async () => {
    if (!examId) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get(`/american-exams/${examId}`);
      setExam(data);
      const loaded = normalizeLoadedQuestions(data.questions || []);
      setQuestions(loaded.length ? loaded : [emptyQuestion('main')]);
      setDirty(loaded.length === 0);
    } catch (e) {
      setError(e.response?.data?.error || 'שגיאה בטעינת המבחן');
      setExam(null);
    } finally {
      setLoading(false);
    }
  }, [examId]);
  useEffect(() => { load(); }, [load]);

  const touch = () => { setDirty(true); setError(''); };
  const updateQuestion = (i, patch) => { setQuestions((prev) => prev.map((q, j) => (j === i ? { ...q, ...patch } : q))); touch(); };
  const updateOption = (i, o, v) => { setQuestions((prev) => prev.map((q, j) => (j === i ? { ...q, options: q.options.map((x, k) => (k === o ? v : x)) } : q))); touch(); };
  const move = (i, d) => {
    const t = i + d;
    if (t < 0 || t >= questions.length) return;
    setQuestions((prev) => { const next = [...prev]; [next[i], next[t]] = [next[t], next[i]]; return next; });
    setDirty(true);
  };
  const remove = (i) => {
    if (questions.length <= 1) { setError('חייבת להישאר לפחות שאלה אחת'); return; }
    setQuestions((prev) => prev.filter((_, j) => j !== i));
    touch();
  };
  const add = (after = null) => {
    setQuestions((prev) => {
      const q = emptyQuestion(prev[after]?.sectionKey || 'main');
      if (after == null || after < 0 || after >= prev.length - 1) return [...prev, q];
      const next = [...prev];
      next.splice(after + 1, 0, q);
      return next;
    });
    touch();
  };

  const validate = () => {
    if (!questions.length) return 'נדרשת לפחות שאלה אחת';
    for (let i = 0; i < questions.length; i += 1) {
      const q = questions[i];
      if (!SECTION_LABELS[q.sectionKey]) return `שאלה ${i + 1}: סעיף לא תקין`;
      if (!q.options.every((o) => String(o || '').trim())) return `שאלה ${i + 1}: יש למלא את כל 3 האפשרויות`;
      if (![1, 2, 3].includes(Number(q.correctOption))) return `שאלה ${i + 1}: יש לבחור תשובה נכונה`;
    }
    if (exam?.status === 'active' && !questions.some((q) => q.sectionKey === 'main')) return 'מבחן פעיל חייב לכלול לפחות שאלה אחת בסעיף הראשי';
    return '';
  };
  const save = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setSaving(true);
    setError('');
    try {
      const payload = { questions: questions.map((q) => ({ sectionKey: q.sectionKey, text: String(q.text || '').trim(), options: q.options.map((o) => String(o || '').trim()), correctOption: Number(q.correctOption) })) };
      const { data } = await api.put(`/american-exams/${examId}/questions`, payload);
      const saved = data.exam || data;
      setExam(saved);
      setQuestions(normalizeLoadedQuestions(saved.questions || payload.questions));
      setDirty(false);
      toast.success(`נשמר · ${payload.questions.length} שאלות`);
    } catch (e) {
      setError(e.response?.data?.error || 'שגיאה בשמירת השאלות');
    } finally {
      setSaving(false);
    }
  };

  const summary = useMemo(() => sectionSummary(questions), [questions]);
  const groups = useMemo(() => navGroups(questions), [questions]);

  // המסך בגובה החלון: הכותרת קבועה, רק השאלות (והסרגל) נגללים
  const hasExam = Boolean(exam);
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const measure = () => {
      const top = el.getBoundingClientRect().top + window.scrollY;
      setScreenHeight(Math.max(380, window.innerHeight - top - 16));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [hasExam, loading]);

  // השאלה שבראש אזור הגלילה — מודגשת בסרגל
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined' || !scrollRef.current) return undefined;
    const observer = new IntersectionObserver((entries) => {
      const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible[0]) setActiveIndex(Number(visible[0].target.dataset.index));
    }, { root: scrollRef.current, rootMargin: '0px 0px -65% 0px' });
    cardRefs.current.slice(0, questions.length).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [questions.length, hasExam, loading]);

  const jumpTo = (i) => {
    setActiveIndex(i);
    cardRefs.current[i]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (!ALLOWED_FRAMES.has(frame)) return <Navigate to="/american-exams/daf_yomi" replace />;
  if (loading) return <div className="ck-empty"><Spinner size={30} /></div>;
  if (!exam) {
    return (
      <EmptyState icon="alert" title={error || 'מבחן לא נמצא'} action={<Button icon="right" onClick={() => navigate(listPath)}>חזרה לרשימה</Button>} />
    );
  }
  if (exam.frame && ALLOWED_FRAMES.has(exam.frame) && exam.frame !== frame) return <Navigate to={`/american-exams/${exam.frame}/${examId}`} replace />;

  const saveLabel = saving ? 'שומר…' : 'שמירת שאלות';
  return (
    <div ref={rootRef} className="nx-aq-screen" style={screenHeight ? { height: screenHeight } : undefined}>
      <div className="nx-aq-screen__head">
        <Button variant="secondary" icon="right" className="nx-aq-back" onClick={() => navigate(listPath)}>
          כל המבחנים — {FRAME_LABELS[exam.frame] || FRAME_LABELS[frame]}
        </Button>
        <PageHeader
          title={exam.title || 'עריכת מבחן'}
          sub={[FRAME_LABELS[exam.frame] || exam.frame, formatMaterial(exam.material, exam.frame), exam.status === 'active' ? 'פעיל' : (exam.status === 'scheduled' ? `עתידי — נפתח ב-${new Date(exam.opensAt).toLocaleDateString('he-IL')}` : 'כבוי'), `${questions.length} שאלות`, summary].filter(Boolean).join(' · ')}
          more={[{ key: 'add', label: 'הוסף שאלה בסוף', icon: 'plus', onClick: () => add(null) }, { key: 'reload', label: 'טען מחדש (ביטול שינויים)', icon: 'refresh', onClick: load }]}
        >
          <Button variant="primary" icon="check" disabled={saving || !dirty} onClick={save}>{saveLabel}</Button>
        </PageHeader>
        {dirty ? <Banner tone="warning" icon="edit">יש שינויים שלא נשמרו.</Banner> : null}
        {error ? <Banner tone="danger" icon="alert" onClose={() => setError('')}>{error}</Banner> : null}
      </div>

      <div className="nx-content-layout nx-aq-screen__body">
        <nav className="nx-sidenav nx-aq-nav" aria-label="שאלות המבחן">
          {groups.map((g) => (
            <div key={`${g.key}-${g.items[0].i}`} className="nx-sidenav__group">
              <div className="nx-sidenav__label">{g.label}</div>
              {g.items.map(({ q, i }) => (
                <button
                  key={i}
                  type="button"
                  className={`nx-sidenav__item nx-aq-nav__item ${activeIndex === i ? 'is-on' : ''} ${isIncomplete(q) ? 'is-bad' : ''}`}
                  title={q.text || `שאלה ${i + 1}`}
                  onClick={() => jumpTo(i)}
                >
                  <b className="nx-aq-nav__num">{i + 1}</b>
                  <span>{q.text || 'ללא טקסט'}</span>
                </button>
              ))}
            </div>
          ))}
          <Button size="sm" variant="ghost" icon="plus" onClick={() => { add(null); setTimeout(() => jumpTo(questions.length), 50); }}>הוסף שאלה</Button>
        </nav>

        <div ref={scrollRef} className="nx-content-main nx-aq-scroll" style={{ gap: 10 }}>
          {questions.map((q, i) => (
            <section
              key={`q-${i}`}
              ref={(el) => { cardRefs.current[i] = el; }}
              data-index={i}
              className={`nx-aq ${activeIndex === i ? 'is-active' : ''}`}
            >
              <div className="nx-aq__head">
                <span className="nx-aq__title">שאלה {i + 1}</span>
                <div className="nx-aq__section"><Select value={q.sectionKey} onChange={(v) => updateQuestion(i, { sectionKey: v })} options={SECTION_OPTIONS} aria-label="סעיף" /></div>
                <IconButton size="sm" icon="up" label="העלה" disabled={i === 0} onClick={() => move(i, -1)} />
                <IconButton size="sm" icon="down" label="הורד" disabled={i === questions.length - 1} onClick={() => move(i, 1)} />
                <IconButton size="sm" icon="plus" label="הוסף שאלה אחרי זו" onClick={() => add(i)} />
                <IconButton size="sm" icon="trash" label="מחק שאלה" onClick={() => remove(i)} />
              </div>
              <div className="nx-aq__text">
                <TextArea rows={2} value={q.text} placeholder="טקסט השאלה" aria-label={`טקסט שאלה ${i + 1}`} onChange={(e) => updateQuestion(i, { text: e.target.value })} />
              </div>
              <span className="nx-aq__hint">אפשרויות תשובה — סמנו את הנכונה</span>
              {[0, 1, 2].map((o) => {
                const on = Number(q.correctOption) === o + 1;
                return (
                  <div key={o} className={`nx-aq__opt ${on ? 'is-on' : ''}`}>
                    <label className="nx-aq__pick">
                      <input type="radio" className="ck-radio" name={`correct-${i}`} checked={on} onChange={() => updateQuestion(i, { correctOption: o + 1 })} aria-label={`אפשרות ${o + 1} נכונה`} />
                      {o + 1}
                    </label>
                    <div style={{ flex: 1, minWidth: 0 }}><TextArea rows={1} value={q.options[o]} placeholder={`אפשרות ${o + 1}`} onChange={(e) => updateOption(i, o, e.target.value)} /></div>
                  </div>
                );
              })}
            </section>
          ))}
          <Row style={{ marginTop: 4 }}>
            <Button icon="plus" onClick={() => add(null)}>הוסף שאלה בסוף</Button>
            <Button variant="primary" icon="check" disabled={saving || !dirty} onClick={save}>{saveLabel}</Button>
          </Row>
        </div>
      </div>
    </div>
  );
}
